import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {ObjectBaseModeComponent} from '../../../../../../../core/shared/object/mvs-object-base/object-base-mode.component';
import {HgImportDto} from '../../../../../model/dto/entity/hg-import.dto';
import {HgImportStatusInternalEnum} from '../../../../../model/dto/enum/hg-import-status-internal.enum';
import {HgGdvSetupService, HgGdvVerifyImportDto} from '../../../../../service/api/hg-gdv-setup.service';
import {MvsMessageService} from '../../../../../../../core/shared/service/message/mvs-message.service';
import {
    MvsObjectNavigationService
} from '../../../../../../../core/shared/navigation/mvs-object-navigation.service';
import {
    MvsObjectNavigationEntry
} from '../../../../../../../core/shared/navigation/mvs-object-navigation-entry';
import {
    MvsObjectNavigationActionEnum
} from '../../../../../../../core/shared/navigation/mvs-object-navigation-action-enum';

/**
 * Full view for the HgImport ObjectBaseComponent.
 *
 * Displays all relevant information about a GDV import:
 *  - Partner + Status + Audit data (header)
 *  - Referenced GDV source document
 *  - Optional runtime snapshot document
 *  - Linked EI-Import with ingestion progress counters
 *  - Error message (only if status === ERROR)
 *  - Action: "Daten verifizieren" (calls HG GDV setup verifyImport endpoint)
 *
 * This view is read-only — there is no edit mode for HgImport.
 */
@Component({
    selector: 'hg-import-object-full',
    templateUrl: './hg-import-object-full.component.html',
    standalone: false
})
export class HgImportObjectFullComponent extends ObjectBaseModeComponent implements OnInit, OnChanges, OnDestroy {

    @Input() override dto: HgImportDto;

    busy: boolean = false;
    override initialized: boolean = false;

    /** Verifizierungsergebnis (falls vorhanden). */
    verifyResult: HgGdvVerifyImportDto | null = null;

    /** Exposed to the template for status comparisons. */
    protected readonly HgImportStatusInternalEnum = HgImportStatusInternalEnum;

    constructor(
        protected route: ActivatedRoute,
        protected hgGdvSetupService: HgGdvSetupService,
        protected messageService: MvsMessageService,
        protected navigationService: MvsObjectNavigationService) {
        super(route);
    }

    ngOnInit(): void {
        super.ngOnInit();
        this.refreshComponent();
    }

    refreshComponent(): void {
        this.initialized = true;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
    }

    ngOnDestroy(): void {
    }

    /**
     * Opens the HgPartner OBC for the partner referenced by this import.
     */
    openPartner(): void {
        if (!this.dto?.hgPartnerDtoId) {
            return;
        }
        const entry = MvsObjectNavigationEntry.createNavigationEntry(
            'hg.HgPartner',
            this.dto.hgPartnerDtoId,
            'object',
            this.dto.hgPartnerDtoName || '',
            '',
            '',
            MvsObjectNavigationActionEnum.edit
        );
        this.navigationService.navigateTo(entry, 'main');
    }

    /**
     * Opens the DM document referenced by this import.
     */
    openDocument(): void {
        if (!this.dto?.dmDocumentDtoId) {
            return;
        }
        const entry = MvsObjectNavigationEntry.createNavigationEntry(
            'dm.DmDocument',
            this.dto.dmDocumentDtoId,
            'object',
            this.dto.dmDocumentDtoName || '',
            '',
            '',
            MvsObjectNavigationActionEnum.edit
        );
        this.navigationService.navigateTo(entry, 'main');
    }

    /**
     * Opens the runtime snapshot document referenced by this import.
     */
    openRuntimeSnapshot(): void {
        if (!this.dto?.runtimeSnapshotDocumentDtoId) {
            return;
        }
        const entry = MvsObjectNavigationEntry.createNavigationEntry(
            'dm.DmDocument',
            this.dto.runtimeSnapshotDocumentDtoId,
            'object',
            this.dto.runtimeSnapshotDocumentDtoName || '',
            '',
            '',
            MvsObjectNavigationActionEnum.edit
        );
        this.navigationService.navigateTo(entry, 'main');
    }

    /**
     * Opens the EiImport OBC referenced by this import.
     */
    openEiImport(): void {
        if (!this.dto?.eiImportDtoId) {
            return;
        }
        const entry = MvsObjectNavigationEntry.createNavigationEntry(
            'ei.EiImport',
            this.dto.eiImportDtoId,
            'object',
            this.dto.eiImportDtoName || '',
            '',
            '',
            MvsObjectNavigationActionEnum.edit
        );
        this.navigationService.navigateTo(entry, 'main');
    }

    /**
     * Triggers the verification endpoint which returns entity counts imported
     * for the related partner. Shows a success/error toast and stores the
     * result so the template can render the counts.
     */
    verifyImport(): void {
        if (this.busy || !this.dto?.hgPartnerDtoId) {
            return;
        }
        this.busy = true;
        this.hgGdvSetupService.verifyImport(this.dto.hgPartnerDtoId).subscribe({
            next: (res) => {
                this.verifyResult = res;
                this.busy = false;
                this.messageService.showSuccessMessage(
                    'Verifizierung erfolgreich',
                    'Die importierten Datensätze wurden gezählt.'
                );
            },
            error: (err) => {
                this.busy = false;
                this.messageService.showErrorMessage(
                    'Verifizierung fehlgeschlagen',
                    err?.error?.message || 'Die Verifizierung konnte nicht durchgeführt werden.'
                );
            }
        });
    }
}
